import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { FolderPlus } from "lucide-react";
import AutocompleteField from "../Form/AutocompleteField";
import {
  GALLERY_FOLDERS_DESTINATION_PATH,
  GALLERY_FOLDERS_NEW_INLINE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";
import {
  buildFolderSelectOptions,
  filterFolderOptions,
} from "../../utils/contentFolderUtils";

const ADD_FOLDER_ACTION_VALUE = "__add-folder__";

const ContentFolderAddOptionLabel = () => {
  const { t } = useTranslationApp();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FolderPlus size={14} />
      <Box component="span">{t(GALLERY_FOLDERS_NEW_INLINE)}</Box>
    </Box>
  );
};

const SelectContentFolder = ({
  control,
  errors = {},
  rules = {},
  name = "folderID",
  folders = [],
  label = "",
  disabled = false,
  disabledFolderID = null,
  variant = "filled",
  fullWidth = true,
  placeholder = "",
  onOpenCreateFolder = () => {},
  showDestinationPath = true,
}) => {
  const { t } = useTranslationApp();

  const autocompleteOptions = useMemo(() => {
    const folderOptions = buildFolderSelectOptions({ folders }).filter(
      (option) => option.value !== disabledFolderID
    );

    return [
      {
        value: ADD_FOLDER_ACTION_VALUE,
        label: t(GALLERY_FOLDERS_NEW_INLINE),
        isActionOption: true,
      },
      ...folderOptions,
    ];
  }, [disabledFolderID, folders, t]);

  const folderID = useWatch({ control, name });

  const selectedPathLabel = useMemo(() => {
    if (!folderID) return null;

    const option = autocompleteOptions.find((item) => item.value === folderID);
    return option?.pathLabel ?? null;
  }, [autocompleteOptions, folderID]);

  const onFilterOptions = (options, { inputValue }) => {
    const addOption = options.find((option) => option.isActionOption);
    const folderOptions = options.filter((option) => !option.isActionOption);

    const filtered = filterFolderOptions({
      options: folderOptions,
      inputValue,
    });

    return addOption ? [addOption, ...filtered] : filtered;
  };

  const onRenderOption = (props, option) => {
    if (option.isActionOption) {
      return (
        <Box {...props} component="li" key={option.value}>
          <ContentFolderAddOptionLabel />
        </Box>
      );
    }

    return (
      <Box {...props} component="li" key={option.value}>
        <Typography
          variant="body1"
          sx={{ color: "background.paperContrastText" }}
        >
          {option.label}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <AutocompleteField
        control={control}
        errors={errors}
        name={name}
        rules={rules}
        label={label}
        options={autocompleteOptions}
        disabled={disabled}
        fullWidth={fullWidth}
        variant={variant}
        placeholder={placeholder}
        filterOptions={onFilterOptions}
        renderOption={onRenderOption}
        disableClearable
        onActionOptionSelect={onOpenCreateFolder}
      />
      {showDestinationPath && selectedPathLabel ? (
        <Chip
          label={t(GALLERY_FOLDERS_DESTINATION_PATH, {
            path: selectedPathLabel,
          })}
          sx={{
            width: "100%",
            height: "auto",
            mt: 2,
            borderRadius: 3,
            backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            "& .MuiChip-label": {
              display: "block",
              whiteSpace: "normal",
              width: "100%",
              py: 1.5,
              px: 1.5,
              fontSize: "0.875rem",
              lineHeight: 1.43,
            },
          }}
        />
      ) : null}
    </Box>
  );
};

export default SelectContentFolder;
